import {ActionError, type ActionResult} from "..";
import {log} from "../util/Log";

export const GEOCODE_ERROR_CODE = {
	EMPTY_QUERY: "E_EMPTY_QUERY",
	QUERY_TOO_LONG: "E_QUERY_TOO_LONG",
	MISSING_API_KEY: "E_MISSING_GEOCODING_API_KEY",
	ZERO_RESULTS: "E_ZERO_RESULTS",
	INVALID_REQUEST: "E_INVALID_REQUEST",
	INVALID_RESPONSE: "E_INVALID_GEOCODE_RESPONSE",
	REQUEST_FAILED: "E_GEOCODING_FAILED",
} as const;

export type GeocodeAddressResult = ActionResult<{
	latitude: number;
	longitude: number;
	/** Present when Google included a display address; not required for success. */
	formattedAddress: string | null;
	/**
	 * Two-letter US state abbreviation when the top result is a state
	 * (`administrative_area_level_1`), whether the user searched "TX" or "Texas".
	 * Null for city / ZIP / other place types.
	 */
	stateCode: string | null;
}>;

export type GoogleGeocodeAddressComponent = {
	long_name?: string;
	short_name?: string;
	types?: string[];
};

export type GoogleGeocodeResult = {
	formatted_address?: string;
	types?: string[];
	address_components?: GoogleGeocodeAddressComponent[];
	geometry?: {
		location?: {
			lat?: number;
			lng?: number;
		};
	};
};

export type GoogleGeocodeResponse = {
	status?: string;
	error_message?: string;
	results?: GoogleGeocodeResult[];
};

/**
 * Two-letter state code when the geocode result itself is a US state.
 */
export function extractStateCodeFromGeocodeResult(
	result: GoogleGeocodeResult | undefined
): string | null {
	if (!result?.types?.includes("administrative_area_level_1")) {
		return null;
	}

	const stateComponent = result.address_components?.find((component) =>
		component.types?.includes("administrative_area_level_1")
	);
	const shortName = stateComponent?.short_name?.trim().toUpperCase();
	if (!shortName || !/^[A-Z]{2}$/.test(shortName)) {
		return null;
	}

	return shortName;
}

/**
 * Map a Google Geocoding JSON body to our {@link ActionResult} shape.
 */
export function mapGeocodeResponse(
	data: GoogleGeocodeResponse
): GeocodeAddressResult {
	const status = data.status;

	if (status === "ZERO_RESULTS") {
		return new ActionError(
			GEOCODE_ERROR_CODE.ZERO_RESULTS,
			"We couldn't find that location. Try a different city, state, or ZIP."
		).toActionResult();
	}

	if (status === "INVALID_REQUEST") {
		log.error(
			{status, errorMessage: data.error_message},
			"Google Geocoding API rejected the request"
		);
		return new ActionError(
			GEOCODE_ERROR_CODE.INVALID_REQUEST,
			"Enter a valid city, state, or ZIP code."
		).toActionResult();
	}

	if (status !== "OK") {
		log.error(
			{status, errorMessage: data.error_message},
			"Google Geocoding API returned an unsuccessful status"
		);
		return new ActionError(
			GEOCODE_ERROR_CODE.REQUEST_FAILED,
			"Failed to look up that location. Please try again."
		).toActionResult();
	}

	const first = data.results?.[0];
	const latitude = first?.geometry?.location?.lat;
	const longitude = first?.geometry?.location?.lng;
	const rawFormattedAddress = first?.formatted_address;
	const formattedAddress =
		typeof rawFormattedAddress === "string" && rawFormattedAddress.trim()
			? rawFormattedAddress
			: null;
	const stateCode = extractStateCodeFromGeocodeResult(first);

	if (
		typeof latitude !== "number" ||
		!Number.isFinite(latitude) ||
		typeof longitude !== "number" ||
		!Number.isFinite(longitude)
	) {
		log.error(
			{hasResult: Boolean(first)},
			"Google Geocoding API OK response missing coordinates"
		);
		return new ActionError(
			GEOCODE_ERROR_CODE.INVALID_RESPONSE,
			"Failed to look up that location. Please try again."
		).toActionResult();
	}

	return {
		success: true,
		latitude,
		longitude,
		formattedAddress,
		stateCode,
	};
}
